import React, {Component} from 'react';
import {withRouter} from "react-router";
import {Button} from 'react-bootstrap';
import Modal from '../../../components/modal/Modal';
import {TextInput} from "../../../components";
import DateUtil from "../../../utils/DateUtil";
import AppointmentUtil from '../../../utils/AppointmentUtil';
import RemotingService from "../../../services/remoting-service/RemotingService";
import NotificationService from "../../../services/notification-service/NotificationService";
import EnumAppointmentStatus from "../enums/EnumAppointmentStatus";
import EnumAppointmentType from "../enums/EnumAppointmentType";
import EnumGender from "../../../enums/EnumGender";
import EnumNoteState from "../enums/EnumNoteState";
import PatientUtil from "../../../utils/PatientUtil";
import SpecialStatusIcons from "../../patient/SpecialStatusIcons";
import StringUtil from "../../../utils/StringUtil";
import AppointmentCptSelector from "../modals/AppointmentCptSelector";
import FormField from "../../../components/form/FormField";
import AppointmentReferralSelector from "../modals/AppointmentReferralSelector";
import FacilitySelector from "../modals/FacilitySelector";
import HoverableDescriptionBox from "../../../components/hoverable-desc-box/HoverableDescriptionBox";

class ViewAppointment extends Component {

    constructor(props) {
        super(props);
        const radiologyReferral = this.props.selectedAppointment.radiologyReferral || {};
        this.state = {
            selectedAppointment: this.props.selectedAppointment,
            cptList: this.props.selectedAppointment.cptList || [],
            hasChanges: false,
            doctorName: radiologyReferral.doctorName || '',
            doctorLicence: radiologyReferral.doctorLicence || '',
            clinicName: radiologyReferral.clinicName || '',
            clinicId: radiologyReferral.clinicId || '',
            hasReferralChanges: false
        }
        this.cptFormFieldRef = null;
        this.referralNameFieldRef = null;
        this.referralClinicIdFieldRef = null;
        this.referralButtonsRef = React.createRef();
    }

    scrollToReferralButtons = () => {
        setTimeout(() => {
            if (this.referralButtonsRef.current) {
                this.referralButtonsRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
            }
        }, 0);
    }

    componentDidMount() {
        this.retrieveLanguages();
    }

    retrieveLanguages = () => {
        RemotingService.getRemoteCall('api/reference/language', null, (result) => {
            let languages = [];
            result.forEach(element => languages.push(element.name));
            this.setState({...this.state, languages: languages})
        });
    }

    handleReferralSelect = (referral) => {
        const radiologyReferral = this.props.selectedAppointment.radiologyReferral || {};
        const originalValues = {
            doctorName: radiologyReferral.doctorName || '',
            doctorLicence: radiologyReferral.doctorLicence || '',
            clinicName: radiologyReferral.clinicName || '',
            clinicId: radiologyReferral.clinicId || ''
        };

        const newClinicName = referral.clinicName || this.state.clinicName || '';
        const newClinicId = referral.clinicId || this.state.clinicId || '';

        const hasReferralChanges =
            referral.doctorName !== originalValues.doctorName ||
            referral.doctorLicence !== originalValues.doctorLicence ||
            newClinicName !== originalValues.clinicName ||
            newClinicId !== originalValues.clinicId;

        const shouldScroll = hasReferralChanges && !this.state.hasReferralChanges;
        this.setState({
            doctorName: referral.doctorName || '',
            doctorLicence: referral.doctorLicence || '',
            clinicName: newClinicName,
            clinicId: newClinicId,
            hasReferralChanges: hasReferralChanges
        }, () => {
            if (shouldScroll) {
                this.scrollToReferralButtons();
            }
        });
    }

    handleFacilitySelect = (facility) => {
        const radiologyReferral = this.props.selectedAppointment.radiologyReferral || {};
        const originalValues = {
            doctorName: radiologyReferral.doctorName || '',
            doctorLicence: radiologyReferral.doctorLicence || '',
            clinicName: radiologyReferral.clinicName || '',
            clinicId: radiologyReferral.clinicId || ''
        };

        const newClinicName = facility.name || '';
        const newClinicId = facility.eclaimId || '';

        const hasReferralChanges =
            this.state.doctorName !== originalValues.doctorName ||
            this.state.doctorLicence !== originalValues.doctorLicence ||
            newClinicName !== originalValues.clinicName ||
            newClinicId !== originalValues.clinicId;

        const shouldScroll = hasReferralChanges && !this.state.hasReferralChanges;
        this.setState({
            clinicName: newClinicName,
            clinicId: newClinicId,
            hasReferralChanges: hasReferralChanges
        }, () => {
            if (shouldScroll) {
                this.scrollToReferralButtons();
            }
        });
    }

    isEditable = () => {
        const status = this.props.selectedAppointment.status;
        return status === 'BOOKED' || status === 'CONFIRMED' || status === 'ARRIVED';
    }

    hasInsuranceRecord = () => {
        return this.props.selectedAppointment.insuranceRecordId != null;
    }

    handleCptSelected = (cptList) => {
        const originalCptIds = (this.props.selectedAppointment.cptList || []).map(cpt => cpt.id).sort();
        const newCptIds = cptList.map(cpt => cpt.id).sort();
        const hasChanges = JSON.stringify(originalCptIds) !== JSON.stringify(newCptIds);

        this.setState({
            cptList: cptList,
            hasChanges: hasChanges
        });
    }

    handleSaveCptList = () => {
        if (this.cptFormFieldRef && !this.cptFormFieldRef.validate()) {
            return;
        }

        const updateDto = {
            cptList: this.state.cptList
        };

        RemotingService.postRemoteCall(
            `api/appointment/${this.props.selectedAppointment.id}/cpt`,
            updateDto,
            (response) => {
                NotificationService.showNotification({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'CPT codes updated successfully'
                });

                this.setState({
                    selectedAppointment: response,
                    cptList: response.cptList || [],
                    hasChanges: false
                });

                if (this.props.onAppointmentUpdated) {
                    this.props.onAppointmentUpdated(response);
                }
            },
            (error) => {
                NotificationService.showNotification({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update CPT codes'
                });
            }
        );
    }

    handleCancelChanges = () => {
        this.setState({
            cptList: [...this.state.selectedAppointment.cptList || []],
            hasChanges: false
        });
    }

    handleReferralFieldChange = (field, value) => {
        const radiologyReferral = this.props.selectedAppointment.radiologyReferral || {};
        const originalValues = {
            doctorName: radiologyReferral.doctorName || '',
            doctorLicence: radiologyReferral.doctorLicence || '',
            clinicName: radiologyReferral.clinicName || '',
            clinicId: radiologyReferral.clinicId || ''
        };

        const newState = {
            ...this.state,
            [field]: value
        };

        const hasReferralChanges =
            newState.doctorName !== originalValues.doctorName ||
            newState.doctorLicence !== originalValues.doctorLicence ||
            newState.clinicName !== originalValues.clinicName ||
            newState.clinicId !== originalValues.clinicId;

        const shouldScroll = hasReferralChanges && !this.state.hasReferralChanges;
        this.setState({
            [field]: value,
            hasReferralChanges: hasReferralChanges
        }, () => {
            if (shouldScroll) {
                this.scrollToReferralButtons();
            }
        });
    }

    handleSaveReferral = () => {
        let isValid = true;
        if (this.referralNameFieldRef && !this.referralNameFieldRef.validate()) {
            isValid = false;
        }
        if (this.referralLicenceFieldRef && !this.referralLicenceFieldRef.validate()) {
            isValid = false;
        }
        if (this.referralClinicFieldRef && !this.referralClinicFieldRef.validate()) {
            isValid = false;
        }
        if (this.referralClinicIdFieldRef && !this.referralClinicIdFieldRef.validate()) {
            isValid = false;
        }
        if (!isValid) {
            return;
        }
        const updateDto = {
            doctorName: this.state.doctorName,
            doctorLicence: this.state.doctorLicence,
            clinicName: this.state.clinicName,
            clinicId: this.state.clinicId
        };

        RemotingService.postRemoteCall(
            `api/appointment/${this.props.selectedAppointment.id}/referral`,
            updateDto,
            (response) => {
                NotificationService.showNotification({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Referral information updated successfully'
                });

                const radiologyReferral = response.radiologyReferral || {};
                this.setState({
                    selectedAppointment: response,
                    doctorName: radiologyReferral.doctorName || '',
                    doctorLicence: radiologyReferral.doctorLicence || '',
                    clinicName: radiologyReferral.clinicName || '',
                    clinicId: radiologyReferral.clinicId || '',
                    hasReferralChanges: false
                });

                if (this.props.onAppointmentUpdated) {
                    this.props.onAppointmentUpdated(response);
                }
            },
            (error) => {
                NotificationService.showNotification({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update referral information'
                });
            }
        );
    }

    handleCancelReferralChanges = () => {
        const radiologyReferral = this.props.selectedAppointment.radiologyReferral || {};
        this.setState({
            doctorName: radiologyReferral.doctorName || '',
            doctorLicence: radiologyReferral.doctorLicence || '',
            clinicName: radiologyReferral.clinicName || '',
            clinicId: radiologyReferral.clinicId || '',
            hasReferralChanges: false
        });
    }

    appointmentViewModalBody = () => {
        if (this.props.selectedAppointment) {
            let openNotes = this.props.selectedAppointment.notes ? this.props.selectedAppointment.notes.filter(note => note.state === 'OPEN') : [];

            return (
                <div style={{minWidth:"500px", maxWidth:"520px"}}>
                    <div className={"container"}>
                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Appointment ID
                            </div>
                            <div className="col-md-8">
                                {this.props.selectedAppointment.id}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Staff Name
                            </div>
                            <div className="col-md-8">
                                {this.props.selectedAppointment.staff.name}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Start - End
                            </div>
                            <div className="col-md-8">
                                {DateUtil.formatDateMMM(this.props.selectedAppointment.start)
                                + ' ' + AppointmentUtil.appointmentTimeSlot(this.props.selectedAppointment)}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Appointment Status
                            </div>
                            <div className="col-md-8">
                                {EnumAppointmentStatus[this.props.selectedAppointment.status].name}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Appointment Type
                            </div>
                            <div className="col-md-8">
                                {EnumAppointmentType[this.props.selectedAppointment.patientType].name}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                MRN
                            </div>
                            <div className="col-md-8">
                                {StringUtil.mrn(this.props.selectedAppointment.patient.mrn)}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Patient Name
                            </div>
                            <div className="col-md-8">
                                {PatientUtil.getPatientName(this.props.selectedAppointment.patient.firstName,
                                    this.props.selectedAppointment.patient.middleName,
                                    this.props.selectedAppointment.patient.lastName)}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Mobile Phone
                            </div>
                            <div className="col-md-8">
                                {this.props.selectedAppointment.patient.mobilePhone}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                ID No
                            </div>
                            <div className="col-md-8">
                                {PatientUtil.getPatientId(this.props.selectedAppointment.patient)}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Gender
                            </div>
                            <div className="col-md-8">
                                {EnumGender[this.props.selectedAppointment.patient.gender]}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Date of Birth
                            </div>
                            <div className="col-md-8">
                                {this.props.selectedAppointment.patient.birthday ? DateUtil.formatDateMMM(this.props.selectedAppointment.patient.birthday) : null}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Age
                            </div>
                            <div className="col-md-8">
                                {this.props.selectedAppointment.patient.birthday ? DateUtil.getAge(this.props.selectedAppointment.patient.birthday) : null}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Direct Billing
                            </div>
                            <div className="col-md-8">
                                {this.props.selectedAppointment.insuranceExists ? "Yes" : "No"}
                            </div>
                        </div>

                        {this.hasInsuranceRecord() &&
                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Procedures
                            </div>
                            <div className="col-md-8">
                                {this.state.selectedAppointment.cptList && this.state.selectedAppointment.cptList.length > 0 ? (
                                    <div className="d-flex flex-wrap" style={{gap: "6px"}}>
                                        {this.state.selectedAppointment.cptList.map((cpt, i) => (
                                            <HoverableDescriptionBox
                                                key={i}
                                                header={cpt.cptCode}
                                                description={cpt.description}
                                            />
                                        ))}
                                    </div>
                                ) : (
                                    <span className="font-italic text-black-50">No procedures assigned.</span>
                                )}
                            </div>
                        </div>
                        }

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Special Status
                            </div>
                            <div className="col-md-8">
                                <div className="d-flex flex-wrap">
                                    <SpecialStatusIcons specialStatusList={this.props.selectedAppointment.patient.specialStatusList}/>
                                </div>
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Desired Start Time
                            </div>
                            <div className="col-md-8">
                                {this.props.selectedAppointment.desiredStart ?
                                    DateUtil.formatDateMMM(new Date(this.props.selectedAppointment.desiredStart)) + ' ' +
                                    AppointmentUtil.formatWithClinicTime(this.props.selectedAppointment.desiredStart, this.props.selectedAppointment.room.clinicName) : ""}
                            </div>
                        </div>

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Room Name
                            </div>
                            <div className="col-md-8">
                                {this.props.selectedAppointment.room.info}
                            </div>
                        </div>

                        {!this.hasInsuranceRecord() &&
                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                CPT Codes
                            </div>
                            <div className="col-md-8">
                                {this.isEditable() ? (
                                    <div>
                                        <FormField
                                            ref={formField => this.cptFormFieldRef = formField}
                                            validateOn={this.state.cptList}
                                            required={true}
                                            requiredMessage="At least one CPT code must be selected"
                                        >
                                            <AppointmentCptSelector
                                                cptList={this.state.cptList}
                                                clinicCountry={this.state.selectedAppointment.room.clinic.country}
                                                roomName={this.state.selectedAppointment.room.name}
                                                staff={this.state.selectedAppointment.staff}
                                                onCptSelected={this.handleCptSelected}
                                            />
                                        </FormField>
                                        {this.state.hasChanges && (
                                            <div style={{marginTop: '10px'}}>
                                                <Button
                                                    variant="success"
                                                    size="sm"
                                                    onClick={this.handleSaveCptList}
                                                    style={{marginRight: '10px'}}
                                                >
                                                    Save
                                                </Button>
                                                <Button
                                                    variant="secondary"
                                                    size="sm"
                                                    onClick={this.handleCancelChanges}
                                                >
                                                    Cancel
                                                </Button>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    this.state.selectedAppointment.cptList && this.state.selectedAppointment.cptList.length > 0 ?
                                        this.state.selectedAppointment.cptList.map((cpt, i) => {
                                            return (<div key={i}>{cpt.cptCode} {cpt.description}</div>)
                                        }) : <span className="font-italic text-black-50">No CPT codes assigned.</span>
                                )}
                            </div>
                        </div>
                        }

                        <div className={"row"} style={{marginBottom: "3px"}}>
                            <div className="col-md-4 dim14-form-label">
                                Notes
                            </div>
                            <div className="col-md-8">
                                {openNotes.length > 0 ?
                                    openNotes.map((note, i) => {
                                        return (<li> {note.text + ' (' + EnumNoteState[note.state] + ')'} </li>)
                                    }) : <span className="font-italic text-black-50">There is no note in open state.</span>
                                }
                            </div>
                        </div>

                        {!this.hasInsuranceRecord() &&
                        <div style={{
                            border: "1px solid #D3D3D3",
                            borderRadius: 8,
                            padding: "12px 15px",
                            marginTop: "15px",
                            marginLeft: "-15px",
                            marginRight: "-15px"
                        }}>
                            <div className={"row"} style={{marginBottom: "8px", marginLeft: "0px"}}>
                                <div className="col-md-12 dim14-form-label">
                                    <h4>Referral</h4>
                                </div>
                            </div>

                            <div className={"row"} style={{marginBottom: "3px", marginLeft: "0px"}}>
                                <div className="col-md-6 dim14-form-label">
                                    Referring Doctor Name<span style={{color: 'red'}}> *</span>
                                </div>
                                <div className="col-md-6">
                                    {this.isEditable() ? (
                                        <FormField
                                            ref={formField => this.referralNameFieldRef = formField}
                                            required
                                            validateOn={this.state.doctorName}
                                            customValidator={(value) => {
                                                if (!value || value.trim() === '') {
                                                    return false;
                                                }
                                                const words = value.trim().split(/\s+/);
                                                return words.length >= 2;
                                            }}
                                            customValidatorMessage="Referring doctor name must contain at least 2 words"
                                        >
                                            <AppointmentReferralSelector
                                                value={this.state.doctorName}
                                                onValueChange={(val) => this.handleReferralFieldChange('doctorName', val)}
                                                onReferralSelect={this.handleReferralSelect}
                                                inputStyle={{width: "100%"}}
                                            />
                                        </FormField>
                                    ) : (
                                        this.props.selectedAppointment.radiologyReferral?.doctorName || <span className="font-italic text-black-50">-</span>
                                    )}
                                </div>
                            </div>

                            <div className={"row"} style={{marginBottom: "3px", marginLeft: "0px"}}>
                                <div className="col-md-6 dim14-form-label">
                                    Referring Doctor Licence<span style={{color: 'red'}}> *</span>
                                </div>
                                <div className="col-md-6">
                                    {this.isEditable() ? (
                                        <FormField
                                            ref={formField => this.referralLicenceFieldRef = formField}
                                            required
                                            validateOn={this.state.doctorLicence}
                                        >
                                            <TextInput 
                                                value={this.state.doctorLicence || ""}
                                                style={{width: "100%", padding: "5px 11px"}}
                                                placeholder="Enter referring doctor licence"
                                                onChange={(value) => this.handleReferralFieldChange('doctorLicence', value)}
                                            />
                                        </FormField>
                                    ) : (
                                        this.props.selectedAppointment.radiologyReferral?.doctorLicence || <span className="font-italic text-black-50">-</span>
                                    )}
                                </div>
                            </div>

                            <div className={"row"} style={{marginBottom: "3px", marginLeft: "0px"}}>
                                <div className="col-md-6 dim14-form-label">
                                    Referring Clinic Name<span style={{color: 'red'}}> *</span>
                                </div>
                                <div className="col-md-6">
                                    {this.isEditable() ? (
                                        <FormField
                                            ref={formField => this.referralClinicFieldRef = formField}
                                            required
                                            validateOn={this.state.clinicName}
                                        >
                                            <FacilitySelector
                                                value={this.state.clinicName}
                                                onValueChange={(value) => this.handleReferralFieldChange('clinicName', value)}
                                                onFacilitySelect={this.handleFacilitySelect}
                                                inputStyle={{width: "100%"}}
                                            />
                                        </FormField>
                                    ) : (
                                        this.props.selectedAppointment.radiologyReferral?.clinicName || <span className="font-italic text-black-50">-</span>
                                    )}
                                </div>
                            </div>

                            <div className={"row"} style={{marginBottom: "0px", marginLeft: "0px"}}>
                                <div className="col-md-6 dim14-form-label">
                                    Referring Clinic Id<span style={{color: 'red'}}> *</span>
                                </div>
                                <div className="col-md-6">
                                    {this.isEditable() ? (
                                        <FormField
                                            ref={formField => this.referralClinicIdFieldRef = formField}
                                            required
                                            validateOn={this.state.clinicId}
                                        >
                                            <TextInput
                                                value={this.state.clinicId || ""}
                                                style={{width: "100%", padding: "5px 11px"}}
                                                placeholder="Enter referring clinic Sheryan ID"
                                                onChange={(value) => this.handleReferralFieldChange('clinicId', value)}
                                            />
                                        </FormField>
                                    ) : (
                                        this.props.selectedAppointment.radiologyReferral?.clinicId || <span className="font-italic text-black-50">-</span>
                                    )}
                                </div>
                            </div>

                            {this.isEditable() && this.state.hasReferralChanges && (
                                <div ref={this.referralButtonsRef} className={"row"} style={{marginTop: "0px", marginLeft: "0px"}}>
                                    <div className="col-md-12" style={{display: 'flex', justifyContent: 'flex-end'}}>
                                        <Button
                                            variant="secondary"
                                            onClick={this.handleCancelReferralChanges}
                                            style={{marginRight: '10px'}}
                                        >
                                            Cancel
                                        </Button>
                                        <Button
                                            variant="success"
                                            onClick={this.handleSaveReferral}
                                        >
                                            Save
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        }
                    </div>
                </div>
            );
        }
        else {
            return <></>;
        }
    }

    handleShowPatientDetail = () => {
        this.props.history.push("/patientdetail/" + this.props.selectedAppointment.patient.id + "/" + this.props.selectedAppointment.id + "/" +
            this.props.selectedView + "/" + DateUtil.formatDate(this.props.selectedDate));
    }

    renderViewAppointmentFooter = () => {
        return (
            <div className="dim14modal dim14modal-footer">
                <Button variant="primary" onClick={() => this.handleShowPatientDetail()}>Appointment Details</Button>
            </div>
        );
    }

    render() {
        return <Modal visible={true}
                      contentStyle={{maxHeight: 600, overflowY: 'auto'}}
                      header={"Appointment - " + (this.props.selectedAppointment ? PatientUtil.getPatientName(this.props.selectedAppointment.patient.firstName,
                          this.props.selectedAppointment.patient.middleName,
                          this.props.selectedAppointment.patient.lastName) : "")}
                      footer={this.renderViewAppointmentFooter()}
                      closeAction={() => this.props.handleViewAppointmentClose()}>
            {this.appointmentViewModalBody()}
        </Modal>
    }

}

export default withRouter(ViewAppointment);